import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Eye, EyeOff, Lock, Sparkles, ChevronDown, ChevronUp, ArrowUpDown, FileText, Award, BookOpen, Video, Users, Calendar } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/**
 * Doctor Content Hub — one screen, one shared collection.
 *
 * Grouped by "family" (Health Content · Academic & Professional · Publications & Books).
 * Per-kind form renders the right extras (publication.journal/year, webinar.date/link, etc.).
 * Locked kinds show an upgrade CTA instead of "Add".
 */

const KIND_LABELS = {
    health_article: "Health Article",
    health_tip: "Health Tip",
    disease_awareness: "Disease Awareness Page",
    vaccination_awareness: "Vaccination Awareness",
    preventive_library: "Preventive Healthcare Item",
    patient_video: "Patient Education Video",
    patient_guide: "Downloadable Patient Guide",
    seasonal_alert: "Seasonal Health Alert",
    webinar: "Webinar",
    conference_presentation: "Conference Presentation",
    cme_participation: "CME Participation",
    faculty_role: "Faculty Role",
    guest_lecture: "Guest Lecture",
    award_recognition: "Award / Recognition",
    medical_association: "Medical Association",
    professional_membership: "Professional Membership",
    research_publication: "Research Publication",
    book_authored: "Book Authored",
    journal_article: "Journal Article",
    // Phase 2c additions
    achievement_milestone: "Achievement Milestone",
    health_package: "Health Package",
    insurance: "Insurance Accepted",
    diagnostic_partner: "Diagnostic Partner",
    corporate_tieup: "Corporate Tie-up",
    social_impact: "Social Impact Activity",
    legacy_item: "Legacy Item",
};

const FAMILY_ICON = {
    "Health Content": FileText,
    "Academic & Professional": Users,
    "Publications & Books": BookOpen,
    "Achievement Timeline": Award,
    "Practice Services": FileText,
    "Social Impact": Users,
    "Digital Legacy": BookOpen,
};

const SOCIAL_CATEGORIES = [
    ["camps", "Medical Camps"],
    ["blood_donation", "Blood Donation Drives"],
    ["ngo", "NGO Activities"],
    ["csr", "CSR Initiatives"],
    ["school_health", "School Health Programs"],
    ["womens_health", "Women's Health Campaigns"],
    ["senior_citizen", "Senior Citizen Programs"],
    ["rural_outreach", "Rural Outreach"],
    ["public_lecture", "Public Lectures"],
];
const LEGACY_CATEGORIES = [
    ["knowledge_repository", "Knowledge Repository"],
    ["memoir", "Professional Memoir"],
    ["retirement_tribute", "Retirement Tribute"],
    ["mentorship_archive", "Mentorship Archive"],
];

// Per-kind extras schema — what fields to render in the form
const EXTRAS_SCHEMA = {
    research_publication: [["journal", "Journal"], ["year", "Year"], ["link", "DOI / URL"]],
    book_authored:        [["publisher", "Publisher"], ["year", "Year"], ["isbn", "ISBN"], ["link", "Buy link"]],
    journal_article:      [["journal", "Journal"], ["year", "Year"], ["link", "DOI / URL"]],
    webinar:              [["date", "Date"], ["link", "Recording / registration link"]],
    conference_presentation: [["event", "Event"], ["date", "Date"], ["venue", "Venue"]],
    cme_participation:    [["event", "Event"], ["date", "Date"], ["credits", "Credits"]],
    faculty_role:         [["organization", "Organization"], ["from_year", "From"], ["to_year", "To (blank = current)"]],
    guest_lecture:        [["organization", "Institution"], ["date", "Date"], ["topic", "Topic"]],
    award_recognition:    [["issuer", "Issuer"], ["year", "Year"]],
    medical_association:  [["org", "Organization"], ["since", "Since"]],
    professional_membership: [["org", "Organization"], ["since", "Since"], ["level", "Level (Member / Fellow / …)"]],
    patient_video:        [["url", "Video URL (YouTube / Vimeo)"]],
    patient_guide:        [["file_url", "PDF file URL"]],
    seasonal_alert:       [["season", "Season / Month"]],
    // Phase 2c additions
    achievement_milestone: [["year", "Year"], ["detail", "Detail (optional)"]],
    health_package:       [["price", "Price (e.g., ₹4,500)"], ["inclusions", "Inclusions"]],
    insurance:            [["provider", "Provider"], ["plans", "Plans covered"]],
    diagnostic_partner:   [["org", "Organization"], ["link", "Website / booking link"]],
    corporate_tieup:      [["org", "Organization"], ["link", "Website / details"]],
    // social_impact + legacy_item use a category dropdown handled below
};

const CATEGORY_SCHEMA = {
    social_impact: { field: "category", label: "Category", options: SOCIAL_CATEGORIES,
                     extras: [["date", "Date"], ["location", "Location"], ["beneficiaries", "Beneficiaries (approx)"]] },
    legacy_item:   { field: "category", label: "Category", options: LEGACY_CATEGORIES,
                     extras: [["year", "Year (optional)"]] },
};

export default function DoctorContentHub() {
    const [kinds, setKinds] = useState(null);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [editItem, setEditItem] = useState(null); // {kind, id?, title, description, media_url, extras, is_visible}
    const [editSnapshot, setEditSnapshot] = useState(null);
    const [openFamilies, setOpenFamilies] = useState({});

    function cancelEdit() {
        const snap = editSnapshot ? JSON.stringify(editSnapshot) : "{}";
        const cur = editItem ? JSON.stringify(editItem) : "{}";
        if (snap !== cur && !window.confirm("Discard unsaved changes?")) return;
        setEditItem(null);
        setEditSnapshot(null);
    }

    async function load() {
        try {
            const [k, c] = await Promise.all([api.get("/me/content/kinds"), api.get("/me/content")]);
            setKinds(k.data);
            setItems(c.data.items || []);
        } catch (e) { toast.error(errMsg(e, "Failed to load")); }
    }
    useEffect(() => { load(); }, []);

    const byFamily = useMemo(() => {
        if (!kinds) return {};
        const out = {};
        for (const k of kinds.kinds) {
            (out[k.family] = out[k.family] || []).push(k);
        }
        return out;
    }, [kinds]);

    const itemsByKind = useMemo(() => {
        const out = {};
        for (const it of items) (out[it.kind] = out[it.kind] || []).push(it);
        return out;
    }, [items]);

    function openAdd(kind) {
        const seed = { kind, title: "", description: "", media_url: "", extras: {}, is_visible: true };
        setEditItem(seed);
        setEditSnapshot(seed);
    }
    function openEdit(item) {
        const seed = { ...item, extras: item.extras || {} };
        setEditItem(seed);
        setEditSnapshot(seed);
    }

    async function saveItem() {
        if (!editItem.title.trim()) { toast.error("Title required"); return; }
        setBusy(true);
        try {
            if (editItem.id) {
                await api.put(`/me/content/${editItem.id}`, editItem);
                toast.success("Item updated");
            } else {
                await api.post("/me/content", editItem);
                toast.success("Item added");
            }
            setEditItem(null);
            setEditSnapshot(null);
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d && typeof d === "object" && d.code === "kind_locked") {
                toast.error(`Locked — requires feature ${d.feature_code}. Upgrade your package.`);
            } else {
                toast.error(errMsg(e, "Save failed"));
            }
        } finally { setBusy(false); }
    }

    async function toggleVisibility(item) {
        const prev = items;
        // Optimistic — flip immediately, revert on error.
        setItems((cur) => cur.map((it) => it.id === item.id ? { ...it, is_visible: !it.is_visible } : it));
        try {
            await api.put(`/me/content/${item.id}`, { ...item, is_visible: !item.is_visible, extras: item.extras || {} });
            toast.success(!item.is_visible ? "Now visible on public page" : "Hidden from public page");
        } catch (e) {
            setItems(prev);
            toast.error(errMsg(e, "Toggle failed — restored"));
        }
    }
    async function deleteItem(item) {
        if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
        const prev = items;
        setItems((cur) => cur.filter((it) => it.id !== item.id));
        try {
            await api.delete(`/me/content/${item.id}`);
            toast.success("Deleted");
        } catch (e) {
            setItems(prev);
            toast.error(errMsg(e, "Delete failed — restored"));
        }
    }

    async function aiAssist() {
        if (!editItem) return;
        // Non-fabricating draft: server pulls doctor's own data. Endpoint is best-effort;
        // if not present, fall back to a friendly notice.
        setBusy(true);
        try {
            const r = await api.post("/me/content/ai-assist", { kind: editItem.kind, title: editItem.title });
            if (r.data?.description) {
                setEditItem({ ...editItem, description: r.data.description });
                toast.success("Draft inserted — review before saving");
            } else {
                toast.info("AI Assist is being upgraded — draft manually for now.");
            }
        } catch {
            toast.info("AI Assist not available yet for this kind.");
        } finally { setBusy(false); }
    }

    if (!kinds) return <DoctorShell><div className="text-[#17212B]/60">Loading…</div></DoctorShell>;

    return (
        <DoctorShell>
            <div className="mb-6" data-testid="content-hub-header">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Content Hub</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Add and manage your public content</h1>
                <p className="text-sm text-[#17212B]/60 mt-1">
                    Every kind below appears as its own section on your public profile
                    when it has visible items. Locked kinds require a package upgrade.
                </p>
            </div>

            {Object.entries(byFamily).map(([family, list]) => {
                const Icon = FAMILY_ICON[family] || FileText;
                const open = openFamilies[family] !== false; // default open
                return (
                    <Card key={family} className="border-[#123B5D]/10 mb-4" data-testid={`family-${family.toLowerCase().replace(/\s/g, '-').replace(/&/g, '')}`}>
                        <CardHeader className="cursor-pointer" onClick={() => setOpenFamilies({...openFamilies, [family]: !open})}>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Icon size={16} className="text-[#0F766E]" />
                                {family}
                                <span className="text-xs text-[#17212B]/50 ml-auto">{list.filter(k => k.unlocked).length}/{list.length} unlocked</span>
                                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </CardTitle>
                        </CardHeader>
                        {open && (
                            <CardContent className="space-y-3">
                                {list.map((k) => {
                                    const kindItems = itemsByKind[k.kind] || [];
                                    return (
                                        <div key={k.kind} className="border border-[#123B5D]/10 rounded-md p-3" data-testid={`kind-${k.kind}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div>
                                                    <div className="text-sm font-semibold text-[#123B5D]">{KIND_LABELS[k.kind]}</div>
                                                    <div className="text-xs text-[#17212B]/50">Gated by {k.feature_code} · {kindItems.length} item{kindItems.length === 1 ? '' : 's'}</div>
                                                </div>
                                                {k.unlocked ? (
                                                    <Button size="sm" onClick={() => openAdd(k.kind)} data-testid={`add-${k.kind}`}>
                                                        <Plus size={13} /> Add
                                                    </Button>
                                                ) : (
                                                    <a href="/pricing" className="inline-flex items-center gap-1 text-xs font-semibold text-[#D97706] border border-[#D97706]/30 bg-[#D97706]/5 rounded px-2 py-1" data-testid={`upgrade-${k.kind}`}>
                                                        <Lock size={11} /> Upgrade to unlock
                                                    </a>
                                                )}
                                            </div>
                                            {kindItems.length === 0 && k.unlocked && (
                                                <div className="text-xs text-[#17212B]/50 italic py-2">No items yet. Add your first — or use AI Assist inside the editor to draft one from your existing profile data.</div>
                                            )}
                                            {kindItems.map((it) => (
                                                <div key={it.id} className="flex items-start gap-2 py-2 border-t border-[#123B5D]/5" data-testid={`item-${it.id}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <div className={`text-sm font-medium ${it.is_visible ? 'text-[#123B5D]' : 'text-[#17212B]/40'}`}>{it.title}</div>
                                                        {it.description && <div className="text-xs text-[#17212B]/60 line-clamp-2">{it.description}</div>}
                                                        {it.extras && Object.keys(it.extras).length > 0 && (
                                                            <div className="text-[10px] text-[#17212B]/50 mt-1 flex gap-3 flex-wrap">
                                                                {Object.entries(it.extras).map(([k2, v]) => v ? <span key={k2}><span className="font-semibold">{k2}:</span> {String(v)}</span> : null)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button onClick={() => toggleVisibility(it)} title={it.is_visible ? "Hide on public page" : "Show on public page"} className="text-[#17212B]/60 hover:text-[#0F766E]" data-testid={`toggle-visible-${it.id}`}>
                                                        {it.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                                                    </button>
                                                    <button onClick={() => openEdit(it)} title="Edit" className="text-[#17212B]/60 hover:text-[#123B5D]" data-testid={`edit-${it.id}`}>
                                                        <Edit3 size={14} />
                                                    </button>
                                                    <button onClick={() => deleteItem(it)} title="Delete" className="text-[#DC2626]/70 hover:text-[#DC2626]" data-testid={`delete-${it.id}`}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </CardContent>
                        )}
                    </Card>
                );
            })}

            <Dialog open={!!editItem} onOpenChange={(o) => !o && cancelEdit()}>
                <DialogContent className="max-w-lg" data-testid="content-editor">
                    <DialogHeader>
                        <DialogTitle>{editItem?.id ? "Edit item" : `Add ${KIND_LABELS[editItem?.kind] || "item"}`}</DialogTitle>
                    </DialogHeader>
                    {editItem && (
                        <div className="space-y-3">
                            <div>
                                <Label>Title *</Label>
                                <Input value={editItem.title} onChange={(e) => setEditItem({...editItem, title: e.target.value})} data-testid="edit-title" />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea rows={4} value={editItem.description || ""} onChange={(e) => setEditItem({...editItem, description: e.target.value})} data-testid="edit-desc" />
                            </div>
                            {(EXTRAS_SCHEMA[editItem.kind] || []).map(([key, label]) => (
                                <div key={key}>
                                    <Label className="text-xs">{label}</Label>
                                    <Input value={editItem.extras?.[key] || ""} onChange={(e) => setEditItem({...editItem, extras: {...editItem.extras, [key]: e.target.value}})} data-testid={`edit-extras-${key}`} />
                                </div>
                            ))}
                            {CATEGORY_SCHEMA[editItem.kind] && (
                                <>
                                    <div>
                                        <Label className="text-xs">{CATEGORY_SCHEMA[editItem.kind].label} *</Label>
                                        <select
                                            className="w-full h-10 px-3 text-sm bg-white border border-[#E5E9F2] rounded-md focus:outline-none focus:border-[#3A80F7]"
                                            value={editItem.extras?.category || ""}
                                            onChange={(e) => setEditItem({...editItem, extras: {...editItem.extras, category: e.target.value}})}
                                            data-testid={`edit-extras-category`}
                                        >
                                            <option value="">— Select —</option>
                                            {CATEGORY_SCHEMA[editItem.kind].options.map(([v, l]) => (
                                                <option key={v} value={v}>{l}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {CATEGORY_SCHEMA[editItem.kind].extras.map(([key, label]) => (
                                        <div key={key}>
                                            <Label className="text-xs">{label}</Label>
                                            <Input value={editItem.extras?.[key] || ""} onChange={(e) => setEditItem({...editItem, extras: {...editItem.extras, [key]: e.target.value}})} data-testid={`edit-extras-${key}`} />
                                        </div>
                                    ))}
                                </>
                            )}
                            <div>
                                <Label className="text-xs">Media URL (optional — image / link)</Label>
                                <Input value={editItem.media_url || ""} onChange={(e) => setEditItem({...editItem, media_url: e.target.value})} placeholder="https://…" data-testid="edit-media" />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="ghost" onClick={aiAssist} disabled={busy} data-testid="edit-ai-assist">
                            <Sparkles size={13} /> AI Assist
                        </Button>
                        <Button variant="ghost" onClick={cancelEdit}>Cancel</Button>
                        <Button onClick={saveItem} disabled={busy} data-testid="edit-save">{busy ? "Saving…" : "Save"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DoctorShell>
    );
}
